import { useState, useEffect, useRef, useCallback } from "react"
import { Link } from "react-router-dom"
import {
  Menu,
  X,
  Check,
  HardHat,
  TrafficCone,
  Building2,
  Briefcase,
  Landmark,
  ClipboardList,
  KanbanSquare,
  Lightbulb,
  type LucideIcon,
} from "lucide-react"
import { Seo } from '@/components/seo/Seo'
import { MarketingBrand } from "@/components/landing/MarketingBrand"
import { HeroProductWindow } from "@/components/landing/HeroProductWindow"
import { PipelineVisual } from "@/components/landing/PipelineVisual"
import { AnalyseVisual } from "@/components/landing/AnalyseVisual"
import { StrategiseVisual } from "@/components/landing/StrategiseVisual"
import { KnowledgeVisual } from "@/components/landing/KnowledgeVisual"
import { AskVisual } from "@/components/landing/AskVisual"
import { WriteVisual, WRITE_CYCLE_MS } from "@/components/landing/WriteVisual"
import { CollaborateVisual, COLLAB_CYCLE_MS } from "@/components/landing/CollaborateVisual"
import { FeatureTabs } from "@/components/landing/FeatureTabs"
import { LandingFooter } from "@/components/landing/LandingFooter"
import { DemoCtaLink } from '@/components/landing/DemoCtaLink'

// ===================
// Data
// ===================

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#how-it-works", label: "How It Works" },
  { href: "#industries", label: "Industries" },
]

const APP_ORIGIN = window.location.hostname === "localhost" ? "" : "https://app.bidcontext.ai"

type Feature = {
  id: string
  label: string
  icon: JSX.Element
  title: string
  description: string
  // CSS aspect-ratio of the visual's frame. Used to size the mobile
  // wrapper so transform: scale() keeps the layout box correct.
  aspectRatio: string
  visual: JSX.Element
  // How long this feature's tab stays active (ms) — matched to its visual's loop.
  durationMs: number
}

const FeatureIcon = ({ paths }: { paths: JSX.Element }) => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {paths}
  </svg>
)

const FEATURES: Feature[] = [
  {
    id: "pipeline",
    label: "Pipeline",
    icon: <FeatureIcon paths={<><rect x="3" y="3" width="7" height="18"/><rect x="14" y="3" width="7" height="11"/></>} />,
    title: "Track every bid from opportunity to submission",
    description: "One workspace for the whole bid pipeline. Switch between list and kanban views to see what's coming in, what's in progress, and what's been won, without spreadsheets or status update calls.",
    aspectRatio: "16 / 11",
    durationMs: 9000, // --pl-total: 9s
    visual: <PipelineVisual />,
  },
  {
    id: "analyse",
    label: "Analyse",
    icon: <FeatureIcon paths={<><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></>} />,
    title: "Break down any tender in seconds",
    description: "Upload PDFs, Word docs, spreadsheets, even a 500-page tender pack. We extract every compliance requirement, evaluation criterion, key date, and risk, ready to act on. No more manual reading.",
    aspectRatio: "16 / 11",
    durationMs: 18000, // --al-t-total: 18s
    visual: <AnalyseVisual />,
  },
  {
    id: "strategise",
    label: "Strategise",
    icon: <FeatureIcon paths={<><path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 5-5"/></>} />,
    title: "Make smarter bid decisions",
    description: "Score every opportunity against weighted criteria. Run a Go/No-Go assessment in minutes, set your win themes, and align the team before a single word is written.",
    aspectRatio: "4 / 3",
    durationMs: 15000, // --st-t-total: 15s
    visual: <StrategiseVisual />,
  },
  {
    id: "knowledge",
    label: "Knowledge",
    icon: <FeatureIcon paths={<><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/></>} />,
    title: "A living library of your best work",
    description: "Past proposals, project case studies, safety certifications, methodology docs, centralised, searchable, and growing with every bid. Never re-write the same paragraph twice.",
    aspectRatio: "16 / 11",
    durationMs: 9000, // --kn-t-total: 9s
    visual: <KnowledgeVisual />,
  },
  {
    id: "ask",
    label: "Ask",
    icon: <FeatureIcon paths={<><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/><circle cx="12" cy="12" r="10"/></>} />,
    title: "Ask your tender or your library, anything",
    description: "\"What's the liquidated damages cap?\" \"Do we have ISO 45001 audit evidence?\" \"What's our zero-LTI record across highway projects?\" Natural-language Q&A across the full tender pack and your evidence library, every answer cited back to source.",
    aspectRatio: "6 / 5",
    durationMs: 11000, // --ak-t-total: 11s
    visual: <AskVisual />,
  },
  {
    id: "write",
    label: "Write",
    icon: <FeatureIcon paths={<><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/></>} />,
    title: "AI Writing Assistant",
    description: "Plan response structures mapped to tender requirements. Draft compliant responses grounded in your evidence. Revise responses with one click. Review drafts for gaps and compliance.",
    aspectRatio: "16 / 11",
    durationMs: WRITE_CYCLE_MS,
    visual: <WriteVisual />,
  },
  {
    id: "collaborate",
    label: "Collaborate",
    icon: <FeatureIcon paths={<><circle cx="12" cy="12" r="3"/><circle cx="19" cy="5" r="2"/><circle cx="5" cy="5" r="2"/><circle cx="19" cy="19" r="2"/><circle cx="5" cy="19" r="2"/></>} />,
    title: "The whole bid team, in one workspace",
    description: "Assign response sections to bid team members. Send for review with a click. Track who's working on what, surface comments inline, and stay in sync with real-time notifications, without the email chain.",
    aspectRatio: "16 / 11",
    durationMs: COLLAB_CYCLE_MS,
    visual: <CollaborateVisual />,
  },
]

const STEPS = [
  { title: "Upload", description: "Drop in the full tender or RFP pack." },
  { title: "Analyse", description: "AI extracts every requirement, criterion, key date, and risk." },
  { title: "Strategise", description: "Score the bid, set your win themes, and align the team." },
  { title: "Draft", description: "Draft, review, and revise with an AI agent." },
  { title: "Export", description: "Export to your own branded Word template." },
]

const SHIFT_ROWS = [
  { before: "Generic AI with no context of your work", after: "AI that knows your tender, win themes, and library" },
  { before: "Score opportunities by gut feel", after: "Go/No-Go decisions backed by weighted criteria" },
  { before: "Dig through scattered proposal files", after: "Past proposals, instantly searchable" },
  { before: "Days drafting and revising manually", after: "Plan, draft, revise, and review with AI in hours" },
  { before: "Read 500-page tender packs line by line", after: "Every requirement, criterion, date, and risk extracted in minutes" },
  { before: "Chase SMEs who never reply", after: "Sections assigned and tracked with real-time notifications" },
]

type IndustryEntry = { title: string; description: string; image: string }
type RoleEntry = { title: string; description: string; icon: LucideIcon }

const INDUSTRIES: IndustryEntry[] = [
  { title: "Building & Construction", description: "Residential, commercial, and industrial work, from Tier 1 head contracts to specialist subcontractor packages.", image: "/landing/industries/construction.jpg" },
  { title: "Infrastructure & Engineering", description: "Civil engineering, transport, utilities, and energy projects with complex technical and compliance requirements.", image: "/landing/industries/infrastructure.jpg" },
  { title: "Real Estate & Facilities", description: "Property management, facilities maintenance, asset management, and integrated workplace services contracts.", image: "/landing/industries/facilities.jpg" },
  { title: "Government Contracts", description: "Federal, state, and local government procurement, from panel arrangements to major capital works programs.", image: "/landing/industries/government.jpg" },
]

const ROLES: RoleEntry[] = [
  { title: "Bid Directors", description: "Run your bid function at scale. Standardise quality, surface bottlenecks, and lift win rates across every opportunity.", icon: Briefcase },
  { title: "Bid Managers", description: "Scale your response capacity without sacrificing quality. Manage multiple concurrent tenders from one workspace.", icon: ClipboardList },
  { title: "Project Managers", description: "Stop choosing between winning new work and delivering it. Draft bids in minutes instead of days, and get your evenings back.", icon: KanbanSquare },
  { title: "Subject Matter Experts", description: "Contribute your expertise to bids without losing days to writing and formatting. Focus on the work you're best at.", icon: Lightbulb },
]

const FAQS = [
  { question: "What is BidContext?", answer: "BidContext is an AI workspace for bid teams. One platform for tender analysis, response drafting, and team collaboration, with AI that has full context of your tender requirements and your evidence library." },
  { question: "Who is BidContext for?", answer: "Bid directors, managers, project managers, and subject matter experts working in construction, infrastructure & engineering, real estate & facilities, and government contracting. Anyone responding to complex, high-stakes tenders." },
  { question: "Is our bid data secure?", answer: "Yes. We use enterprise-grade encryption and isolated data silos. Your tenders, library, and drafts stay within your organisation and aren't shared with anyone else." },
  { question: "Is our data used to train your AI models?", answer: "No. Your tenders, library, and drafts are isolated to your organisation. We don't use customer data to train any model, and you can delete everything on request." },
  { question: "What document formats are supported?", answer: "PDF, Word, Excel, PowerPoint, CSV, plain text, and images. Scanned documents are supported too. We extract text automatically." },
  { question: "How do you prevent AI hallucinations?", answer: "Our AI only drafts using your provided source documents. Every claim in a draft is accompanied by a clickable citation back to your original source files." },
  { question: "Do I have to run tender analysis to write a response?", answer: "No. Both the tender analysis and strategy steps are optional. For smaller proposals and short RFPs, you can skip straight to drafting with your library and brief. BidContext scales from one-page responses to multi-hundred-page submissions." },
  { question: "Can my whole team collaborate?", answer: "Yes. You can assign sections to different writers, request reviews, and track progress across your team from one dashboard." },
  { question: "What types of responses can I create?", answer: "Multi-section narratives (like a technical submission), single documents, and scored questionnaires with word limits per question." },
  { question: "Can I export my responses?", answer: "Yes. Export to Microsoft Word with your own branded templates. Logos, headers, and fonts all preserved." },
]

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
}

const ARROW = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

// ===================
// Component
// ===================

export function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [headerBg, setHeaderBg] = useState<"white" | "alt" | "dark">("white")
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const hamburgerRef = useRef<HTMLButtonElement>(null)

  // Mobile menu close-on-outside-click
  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (
      mobileMenuRef.current &&
      !mobileMenuRef.current.contains(e.target as Node) &&
      hamburgerRef.current &&
      !hamburgerRef.current.contains(e.target as Node)
    ) {
      setMobileMenuOpen(false)
    }
  }, [])

  useEffect(() => {
    if (!mobileMenuOpen) return
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [mobileMenuOpen, handleOutsideClick])

  // Sticky-nav scroll-collapse + scroll-driven feature tab activation
  useEffect(() => {
    let ticking = false
    const update = () => {
      // Scroll-collapse wordmark
      setScrolled(window.scrollY > 20)

      // Update header background to match the section currently below it
      const HEADER_H = 56
      const probe = window.scrollY + HEADER_H + 1
      const sections = document.querySelectorAll<HTMLElement>("[data-section-bg]")
      let foundBg: "white" | "alt" | "dark" = "white"
      sections.forEach((s) => {
        const top = s.offsetTop
        const bottom = top + s.offsetHeight
        if (probe >= top && probe < bottom) {
          const bg = s.dataset['sectionBg']
          if (bg === "alt" || bg === "white" || bg === "dark") foundBg = bg
        }
      })
      setHeaderBg((prev) => (prev === foundBg ? prev : foundBg))

      ticking = false
    }
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update)
        ticking = true
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    update()
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  // Smooth-scroll for in-page hash links. Needed because React Router's
  // ScrollRestoration (in routes.tsx) intercepts URL changes and overrides
  // the browser's native anchor scroll, causing first-click failures.
  const handleAnchorNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith("#")) return
    e.preventDefault()
    const target = document.getElementById(href.slice(1))
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" })
      window.history.replaceState(null, "", href)
    }
  }

  return (
    <div className="landing-page min-h-screen scroll-smooth">
      <Seo
        title="BidContext - AI Bid Writing & Tender Response Software"
        description="AI bid writing software that analyses your tenders, retrieves evidence from past proposals, and drafts compliant responses in minutes. Every claim cited back to source. Free early access."
        canonical="/"
        jsonLd={FAQ_JSONLD}
      />
      {/* Navigation */}
      <header
        id="lp-site-header"
        className={`sticky top-0 z-50 transition-colors duration-200 ${scrolled ? "scrolled" : ""}`}
        style={{
          background:
            headerBg === "alt"
              ? "var(--lp-bg-alt)"
              : headerBg === "dark"
              ? "var(--lp-footer-bg)"
              : "var(--lp-bg)",
          color: headerBg === "dark" ? "white" : undefined,
        }}
      >
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <MarketingBrand />
          </Link>

          <nav
            className="hidden md:flex items-center gap-8 text-sm font-medium absolute inset-y-0 left-1/2 -translate-x-1/2"
            style={{ color: "var(--lp-fg)" }}
          >
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleAnchorNav(e, link.href)}
                className="hover:opacity-70 transition-opacity"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <a
              href={`${APP_ORIGIN}/login`}
              className="lp-btn-ghost inline-flex items-center h-9 px-4 rounded-md text-sm font-medium transition-all"
            >
              Log in
            </a>
            <DemoCtaLink
              location="nav"
              className="lp-btn-primary inline-flex items-center h-9 px-4 rounded-md text-sm font-medium"
            >
              Book a demo
            </DemoCtaLink>
          </div>

          {/* Mobile hamburger */}
          <button
            ref={hamburgerRef}
            className="md:hidden p-2 -mr-2"
            style={{ color: "var(--lp-muted)" }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div ref={mobileMenuRef} className="absolute top-14 left-0 right-0 md:hidden border-t lp-hairline bg-white shadow-lg">
            <div className="px-6 py-3 space-y-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block py-2 text-sm font-medium transition-colors"
                  style={{ color: "var(--lp-muted)" }}
                  onClick={(e) => {
                    setMobileMenuOpen(false)
                    handleAnchorNav(e, link.href)
                  }}
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-3 border-t lp-hairline flex flex-col gap-2">
                <a
                  href={`${APP_ORIGIN}/login`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="lp-btn-secondary w-full text-center px-3 py-2 rounded-md text-sm font-medium"
                >
                  Log in
                </a>
                <DemoCtaLink
                  location="mobile_nav"
                  onClick={() => setMobileMenuOpen(false)}
                  className="lp-btn-primary w-full text-center px-3 py-2 rounded-md text-sm font-medium"
                >
                  Book a demo
                </DemoCtaLink>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* ============== HERO ============== */}
        <section className="relative overflow-hidden" data-section-bg="white">
          <div className="lp-hero-bg" aria-hidden="true" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 pt-24 pb-12 text-center">
            <div className="lp-chip inline-flex items-center px-3 py-1 rounded-[10px] text-xs font-medium mb-8">
              AI-powered bid intelligence
            </div>
            <h1 className="lp-hero-title text-[42px] sm:text-[54px] md:text-[64px] lg:text-[72px] leading-[1.0] mb-7 max-w-4xl mx-auto">
              AI workspace for smarter bids
            </h1>
            <p
              className="text-lg leading-relaxed max-w-2xl mx-auto mb-10"
              style={{ color: "#646B76" }}
            >
              Analyse tenders, draft compliant responses, and collaborate as a team, with AI that
              knows your business. Respond faster. Win more.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <DemoCtaLink
                location="hero"
                className="lp-btn-primary w-full sm:w-auto px-5 py-3 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2"
              >
                Book a demo
                {ARROW}
              </DemoCtaLink>
            </div>
          </div>

          {/* Animated hero product window — desktop only. The mock is a fixed
              ~720×560 grid that overflows on phones, and a scaled-down version
              would be unreadable. Mobile users see headline + CTA only. */}
          <div className="relative z-10 hidden md:block max-w-6xl mx-auto px-6 pb-20">
            <div className="lp-hero-frame">
              <HeroProductWindow />
            </div>
          </div>
        </section>

        {/* Trust strip — industry framing (pre-launch, no customer logos) */}
        <section data-section-bg="white" className="border-y" style={{ borderColor: "var(--lp-border)" }}>
          <div className="max-w-5xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-4 text-center">
            <span className="text-xs uppercase tracking-wider font-medium shrink-0" style={{ color: "var(--lp-muted)", opacity: 0.75 }}>
              Built for Australian bid teams across
            </span>
            <div className="flex items-center gap-x-7 gap-y-3 flex-wrap justify-center">
              {[
                { label: "Construction", icon: HardHat },
                { label: "Infrastructure", icon: TrafficCone },
                { label: "Facilities", icon: Building2 },
                { label: "Government", icon: Landmark },
              ].map(({ label, icon: Icon }) => (
                <span key={label} className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--lp-fg)" }}>
                  <Icon className="h-4 w-4" strokeWidth={1.75} style={{ color: "var(--lp-muted)" }} />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ============== FEATURES (tabbed browser) ============== */}
        <section id="features" data-section-bg="white" className="py-28">
          <div className="max-w-6xl mx-auto px-6">
            <div className="mb-10 md:mb-12">
              <span
                className="inline-flex items-center rounded-[10px] px-3 py-1 text-[11px] font-medium uppercase tracking-wider mb-5"
                style={{ background: "var(--lp-card)", border: "1px solid var(--lp-border)", color: "var(--lp-muted)" }}
              >
                Features
              </span>
              <h2 className="lp-display text-[32px] md:text-[44px] leading-[1.1]">
                Everything you need for the next win
              </h2>
            </div>
            <FeatureTabs features={FEATURES} />
          </div>
        </section>

        {/* ============== HOW IT WORKS ============== */}
        <section id="how-it-works" data-section-bg="white" className="max-w-6xl mx-auto px-6 py-28">
          <div className="mb-12 md:mb-16">
            <span
              className="inline-flex items-center rounded-[10px] px-3 py-1 text-[11px] font-medium uppercase tracking-wider mb-5"
              style={{ background: "var(--lp-card)", border: "1px solid var(--lp-border)", color: "var(--lp-muted)" }}
            >
              How it works
            </span>
            <h2 className="lp-display text-[32px] md:text-[44px] leading-[1.1]">
              From tender pack to submission
            </h2>
          </div>
          <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-x-6 gap-y-10">
            {/* connector line — turns the steps into a left-to-right flow on desktop */}
            <div
              aria-hidden="true"
              className="hidden lg:block absolute left-[10%] right-[10%] top-[21px] h-px"
              style={{ background: "var(--lp-border)" }}
            />
            {STEPS.map((step, i) => (
              <div key={step.title} className="relative z-10 text-center">
                <div className="lp-step-num mx-auto mb-5">{i + 1}</div>
                <h3 className="lp-display-md text-base mb-2">{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: "var(--lp-muted)" }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ============== BID TEAMS RECLAIMED ============== */}
        <section data-section-bg="alt" className="py-28" style={{ background: "var(--lp-bg-alt)" }}>
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-5">
              <span
                className="inline-flex items-center rounded-[10px] px-3 py-1 text-[11px] font-medium uppercase tracking-wider"
                style={{ background: "var(--lp-card)", border: "1px solid var(--lp-border)", color: "var(--lp-muted)" }}
              >
                The shift
              </span>
            </div>
            <h2 className="lp-display text-[40px] md:text-5xl leading-[1.1] text-center mb-16">
              Bidding without the chaos
            </h2>

            <div className="grid md:grid-cols-2 gap-5 md:gap-6 items-stretch">
              {/* Before — the manual grind, visually recessed */}
              <div className="lp-shift-card lp-shift-before">
                <div className="lp-shift-card-head">
                  <span className="lp-shift-tag lp-shift-tag-before">Before</span>
                  <span className="lp-shift-card-title">The manual grind</span>
                </div>
                <ul className="lp-shift-list">
                  {SHIFT_ROWS.map((row) => (
                    <li key={row.before} className="lp-shift-item">
                      <span className="lp-shift-ico lp-shift-ico-x">
                        <X className="w-3 h-3" strokeWidth={3} />
                      </span>
                      <span>{row.before}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* With BidContext — elevated white card, violet accent */}
              <div className="lp-shift-card lp-shift-after">
                <div className="lp-shift-card-head">
                  <span className="lp-shift-tag lp-shift-tag-after">With BidContext</span>
                  <span className="lp-shift-card-title">One AI workspace</span>
                </div>
                <ul className="lp-shift-list">
                  {SHIFT_ROWS.map((row) => (
                    <li key={row.after} className="lp-shift-item lp-shift-item-after">
                      <span className="lp-shift-ico lp-shift-ico-check">
                        <Check className="w-3 h-3" strokeWidth={3.5} />
                      </span>
                      <span>{row.after}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6 mt-16 pt-12 border-t lp-hairline">
              <div>
                <div className="lp-display text-4xl mb-1" style={{ color: "var(--lp-primary)" }}>5+</div>
                <div className="text-sm" style={{ color: "var(--lp-muted)" }}>
                  days saved per bid
                </div>
              </div>
              <div>
                <div className="lp-display text-4xl mb-1" style={{ color: "var(--lp-primary)" }}>100%</div>
                <div className="text-sm" style={{ color: "var(--lp-muted)" }}>
                  citations to source
                </div>
              </div>
              <div>
                <div className="lp-display text-4xl mb-1" style={{ color: "var(--lp-primary)" }}>0</div>
                <div className="text-sm" style={{ color: "var(--lp-muted)" }}>
                  compliance gaps missed
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============== INDUSTRIES ============== */}
        <section id="industries" data-section-bg="white" className="max-w-7xl mx-auto px-6 py-28">
          <div className="text-center mb-16">
            <h2 className="lp-display text-[40px] md:text-5xl leading-[1.1] mb-4">
              Built for complex, high-stakes tenders
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: "var(--lp-muted)" }}>
              Designed for teams where tenders are complex, stakes are high, and the quality of
              your response makes the difference.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {INDUSTRIES.map((industry) => (
              <div
                key={industry.title}
                className="lp-photo-card"
                style={{ backgroundImage: `url('${industry.image}')` }}
              >
                <div className="lp-photo-card-body">
                  <h3 className="lp-photo-card-title">{industry.title}</h3>
                  <p className="lp-photo-card-desc">{industry.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ============== ROLES ============== */}
        <section data-section-bg="alt" className="py-28" style={{ background: "var(--lp-bg-alt)" }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="lp-display text-[40px] md:text-5xl leading-[1.1] mb-4">
                Designed for every role on the bid team
              </h2>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: "var(--lp-muted)" }}>
                Whether you own the bid process or juggle it alongside project delivery,
                BidContext gives you hours back every week.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {ROLES.map((role) => {
                const Icon = role.icon
                return (
                  <div key={role.title} className="lp-rolecard">
                    <div className="lp-rolecard-icon">
                      <Icon className="w-6 h-6" strokeWidth={1.75} />
                    </div>
                    <div>
                      <h3 className="lp-icard-title">{role.title}</h3>
                      <p className="lp-icard-desc">{role.description}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* ============== FAQ ============== */}
        <section data-section-bg="white" className="py-28">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="lp-display text-[40px] md:text-5xl leading-[1.1]">
                Frequently asked questions
              </h2>
            </div>
            <div className="space-y-3">
              {FAQS.map((faq) => (
                <details
                  key={faq.question}
                  className="border lp-hairline rounded-2xl overflow-hidden"
                  style={{ background: "var(--lp-card)" }}
                >
                  <summary className="flex justify-between items-center p-5">
                    <span className="font-medium">{faq.question}</span>
                    <svg
                      className="lp-chev transition-transform"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </summary>
                  <div
                    className="px-5 pb-5 text-sm leading-relaxed"
                    style={{ color: "var(--lp-muted)" }}
                  >
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ============== FINAL CTA ============== */}
        <section data-section-bg="white" className="py-28">
          <div className="max-w-5xl mx-auto px-6">
            <div className="lp-cta-card">
              <h2 className="lp-display text-[34px] md:text-[44px] leading-[1.1] mb-4">
                Ready to transform how you bid?
              </h2>
              <p
                className="text-lg leading-relaxed max-w-xl mx-auto mb-8"
                style={{ color: "rgba(255,255,255,0.72)" }}
              >
                Let us show you what BidContext can do for your team.
              </p>
              <DemoCtaLink
                location="final_cta"
                className="lp-btn-primary px-5 py-3 rounded-md text-sm font-medium inline-flex items-center gap-2"
              >
                Book a demo
                {ARROW}
              </DemoCtaLink>
            </div>
          </div>
        </section>
      </main>

      {/* ============== FOOTER ============== */}
      <LandingFooter />
    </div>
  )
}
